import { useTranslation } from "react-i18next";
import { Box, Button, Divider, IconButton, LinearProgress, Typography } from "@mui/material";

import { CloseButtonIcon } from "../../../assets/icons/gino-icons";
import { GinoCreditsData } from "../../../api/services/ginoCredits";

export const CreditsInfoStep = ({
  onClose,
  ginoCredits: { balance, charged, used },
  onNextStep,
}: {
  onClose: () => void;
  ginoCredits: GinoCreditsData;
  onNextStep: () => void;
}) => {
  const { t } = useTranslation("gino", { keyPrefix: "component.ginoCreditModal" });

  const usedPercentage = ((balance / charged) * 100).toFixed(0);
  const usedPercentageValue = Number(usedPercentage) || 0;

  const creditLimitReached = used >= charged || balance <= 5;

  return (
    <Box sx={styles.modalContent}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{t("title")}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseButtonIcon />
        </IconButton>
      </Box>
      <Divider flexItem orientation="horizontal" />
      {creditLimitReached && (
        <Box sx={styles.creditLimitReachedContainer}>
          <Typography sx={styles.creditLimitReachedTitle}>{t("creditLimitReached")}</Typography>
          <Typography sx={styles.creditLimitReachedDescription}>{t("creditLimitReachedDescription")}</Typography>
        </Box>
      )}
      <Box sx={styles.creditContainer}>
        <Box sx={styles.creditInfo}>
          <Typography sx={styles.creditTitle}>
            {t("spent")}
            <Typography component="span" sx={styles.creditValue}>
              {used}
            </Typography>
          </Typography>
          <Typography sx={styles.creditTitle}>
            {t("limit")}
            <Typography component="span" sx={styles.creditValue}>
              {charged}
            </Typography>
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={usedPercentageValue} />
      </Box>
      <Divider flexItem orientation="horizontal" />
      <Box sx={styles.buttonContainer}>
        <Button variant="primary-md" onClick={onNextStep}>
          {t("addAddOns")}
        </Button>
      </Box>
    </Box>
  );
};

const styles = {
  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "640px",
    borderRadius: "var(--radius-xl, 12px)",
    background: "var(--Colors-Background-bg-primary, #FFF)",
    boxShadow:
      "0 20px 24px -4px var(--Colors-Effects-Shadows-shadow-xl_01, rgba(16, 24, 40, 0.08)), 0 8px 8px -4px var(--Colors-Effects-Shadows-shadow-xl_02, rgba(16, 24, 40, 0.03))",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 24px",
  },
  title: {
    color: "var(--colors-text-text-primary-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  creditContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "12px 24px",
  },
  creditTitle: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  creditValue: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  creditInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "12px 24px",
  },
  creditLimitReachedContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "12px 24px 28px",
  },
  creditLimitReachedTitle: {
    color: "var(--Dark---1000, #111)",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "32px",
    fontStyle: "normal",
    fontWeight: 750,
    lineHeight: "normal",
  },
  creditLimitReachedDescription: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    textAlign: "center",
  },
};
